/**
 * ComparisonView - Side-by-side output comparison with selection
 *
 * Shows outputs from Looper iterations in a grid layout.
 * Users can select/deselect items for merging via checkboxes.
 *
 * Features:
 * - Grid display with up to 3 columns
 * - Click to toggle selection
 * - Select All / Clear buttons
 * - Data preview with truncation
 * - Timestamp display
 */

import type { JoinerInput } from '@deep-agents/shared-types';

// ============================================================================
// PROPS
// ============================================================================

interface ComparisonViewProps {
  /** Array of inputs to display */
  inputs: JoinerInput[];

  /** Currently selected input IDs */
  selectedIds: string[];

  /** Called when selection changes */
  onSelectionChange: (ids: string[]) => void;

  /** Maximum height for the scrollable area */
  maxHeight?: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ComparisonView displays inputs in a side-by-side grid with selection.
 *
 * Each input card shows:
 * - Selection checkbox (top-right)
 * - Input name/label
 * - Data preview (truncated)
 * - Timestamp
 *
 * Clicking anywhere on the card toggles selection.
 */
export function ComparisonView({
  inputs,
  selectedIds,
  onSelectionChange,
  maxHeight = 200,
}: ComparisonViewProps) {
  // Toggle selection for a single input
  const toggleSelection = (id: string) => {
    if (selectedIds.includes(id)) {
      // Remove from selection
      onSelectionChange(selectedIds.filter(i => i !== id));
    } else {
      // Add to selection
      onSelectionChange([...selectedIds, id]);
    }
  };

  // Select all inputs
  const selectAll = () => {
    onSelectionChange(inputs.map(i => i.id));
  };

  // Clear all selections
  const selectNone = () => {
    onSelectionChange([]);
  };

  // Empty state when no inputs yet
  if (inputs.length === 0) {
    return (
      <div className="text-center py-6 text-gray-400 text-sm">
        No inputs received yet
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Selection controls header */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">
          {selectedIds.length} of {inputs.length} selected
        </span>
        <div className="flex gap-2">
          <button
            onClick={selectAll}
            className="text-indigo-600 hover:text-indigo-700"
          >
            Select All
          </button>
          <button
            onClick={selectNone}
            className="text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Side-by-side grid of input cards */}
      <div
        className="grid gap-2 overflow-y-auto"
        style={{
          maxHeight,
          // Show up to 3 columns, fewer if less inputs
          gridTemplateColumns: `repeat(${Math.min(inputs.length, 3)}, 1fr)`,
        }}
      >
        {inputs.map((input) => {
          const isSelected = selectedIds.includes(input.id);

          // Create data preview (truncate long content)
          const dataPreview = typeof input.data === 'string'
            ? input.data.slice(0, 100)
            : JSON.stringify(input.data).slice(0, 100);

          return (
            <div
              key={input.id}
              onClick={() => toggleSelection(input.id)}
              className={`
                relative p-3 rounded-lg border-2 cursor-pointer
                transition-all hover:shadow-md
                ${isSelected
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }
              `}
            >
              {/* Selection checkbox indicator */}
              <div
                className={`
                  absolute top-2 right-2 w-5 h-5 rounded-full
                  flex items-center justify-center
                  ${isSelected
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white border border-gray-300'
                  }
                `}
              >
                {isSelected && (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              {/* Input label/name */}
              <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">
                {input.name}
              </div>

              {/* Data preview */}
              <div className="text-xs text-gray-700 font-mono break-all">
                {dataPreview}
                {dataPreview.length >= 100 && '...'}
              </div>

              {/* Timestamp */}
              <div className="mt-2 text-[9px] text-gray-400">
                {new Date(input.timestamp).toLocaleTimeString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
